import React from 'react';
import { Helmet } from 'react-helmet';
import { Box, Heading, Text, Button } from '@indeed/ifl-components';
import { GettextInterface } from '@indeed/indeed-gettext';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';

export interface ReviewPageProps {
    step?: string;
    gt?: GettextInterface;
}

const ReviewPage: React.FunctionComponent<ReviewPageProps> = () => {
    const { goBack, canGoBack, goForward } = usePageFlow();

    const handleBack = () => {
        void goBack(); // Use flow-aware back navigation
    };

    const handleSubmit = () => {
        // In a real app, this would submit the application to the API
        // Use flow-aware navigation to proceed to post-apply page
        void goForward();
    };

    return (
        <PageTransition pageKey="review">
            <Helmet>
                <title>Review Application - Indeed</title>
                <meta
                    name="description"
                    content="Review and submit your complete job application with Indeed."
                />
                <meta
                    name="keywords"
                    content="review application, submit application, job application, Indeed, careers"
                />
            </Helmet>
            <Box
                sx={{
                    maxInlineSize: '58rem',
                    margin: '0 auto',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: 'background.default',
                        padding: 6,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'border.subtle',
                    }}
                >
                    <Heading
                        as="h2"
                        level={2}
                        sx={{
                            color: 'foreground.primary',
                            marginBlockEnd: 2,
                        }}
                    >
                        Review Your Application
                    </Heading>
                    <Text sx={{ color: 'foreground.secondary', fontSize: 2, marginBlockEnd: 6 }}>
                        Please review all information before submitting your application.
                    </Text>

                    <Box sx={{ marginBlockEnd: 6 }}>
                        <Heading
                            as="h3"
                            level={4}
                            sx={{ marginBlockEnd: 4, color: 'foreground.primary' }}
                        >
                            Contact Information
                        </Heading>
                        <Text sx={{ color: 'foreground.secondary' }}>
                            [Contact information will be displayed here]
                        </Text>
                    </Box>

                    <Box sx={{ marginBlockEnd: 6 }}>
                        <Heading
                            as="h3"
                            level={4}
                            sx={{ marginBlockEnd: 4, color: 'foreground.primary' }}
                        >
                            Resume & Experience
                        </Heading>
                        <Text sx={{ color: 'foreground.secondary' }}>
                            [Resume information will be displayed here]
                        </Text>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
                        {canGoBack && (
                            <Button variant="secondary" onClick={handleBack}>
                                Back
                            </Button>
                        )}
                        <Button variant="primary" onClick={handleSubmit}>
                            Submit Application
                        </Button>
                    </Box>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default ReviewPage;
